import { DictType } from "@pma/types"
import { useAuth } from "@stores/auth-store"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { PlusCircle } from "lucide-react"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Separator } from "@components/common/separator"
import { useContext, useEffect, useState } from "react"
import { Button } from "@components/common/button"
import useTranslator from "@hooks/use-translator"
import { SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useModal } from "@stores/modal-store"
import { Input } from "@components/common/input"
import { Checkbox } from "@components/common/checkbox"
import { Label } from "@components/common/label"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props {
  taskId: number
  responsible: Array<DictType> | Array<number>
  parentTaskId?: number
  tableView?: boolean
}
const AssignToUser = ({ taskId, responsible, parentTaskId, tableView = false }: Props) => {
  const modal = useModal()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)
  const responsibleArr = responsible || []
  const organizationUsers = authConfig?.organizationUsers || []

  const [searchText, setSearchText] = useState<string>("")
  const [selectedUser, setSelectedUser] = useState<DictType>({})
  const [isAssignUserList, setIsAssignUserList] = useState<Boolean>(false)
  const [availableUsersArr, setAvailableUserArr] = useState(organizationUsers)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set([]))

  useEffect(() => {
    if (responsible) {
      setSelectedRows(
        new Set(responsible.map((item) => (typeof item === "number" ? item : item.id))),
      )
    }
  }, [responsible])

  const assignUser = async (taskId: number, users: number[]) => {
    const response = await axios.post(
      SERVER_URL + "/tasks/edit/byCurrentUser",
      {
        responsible_id: users,
        task_id: taskId,
        inform_users: true,
      },
      {
        headers: {
          Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
        },
      },
    )

    return response.data
  }

  const assignTaskMutation = useMutation({
    mutationKey: ["assign-task-to-users"],
    mutationFn: (users: number[]) => assignUser(taskId, users),
    onSuccess: (data) => {
      //Trigger Revalidate tasks
      queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "User not invited") {
        promptReInvite()
      }
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const promptReInvite = () => {
    modal.onOpen()
    modal.setContext("invite")
    modal.setMetaData(selectedUser)
  }

  const handleCheckboxChange = (id: number) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(id)) newSelected.delete(id)
      else newSelected.add(id)

      return newSelected
    })
  }

  const handleSubmit = () => {
    const idArray = Array.from(selectedRows)
    assignTaskMutation.mutate(idArray)
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = organizationUsers.filter((user: DictType) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()),
      )
      setAvailableUserArr(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  return (
    <Popover>
      <div className='flex w-full items-center'>
        <div className='flex flex-col items-center gap-0.5'>
          {responsibleArr?.slice(0, 2).map((user: any) => {
            return tableView ? (
              <div className='flex gap-1 rounded-full border px-1.5 py-1'>
                <Avatar className='h-5 w-5'>
                  <AvatarImage
                    src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                  />
                </Avatar>
                <p className='font-medium'>{user?.name}</p>
              </div>
            ) : (
              <p className='font-medium'>{user?.name}</p>
            )
          })}
          <div className='flex items-center gap-1'>
            {responsibleArr?.length > 2 && (
              <PopoverTrigger onClick={() => setIsAssignUserList(true)}>
                <div className='relative h-5 w-5 rounded-full bg-blue-100'>
                  +{responsibleArr.length - 2}
                </div>
              </PopoverTrigger>
            )}
            <PopoverTrigger onClick={() => setIsAssignUserList(false)}>
              <PlusCircle className='mx-0.5' size={20} />
            </PopoverTrigger>{" "}
          </div>
        </div>
      </div>

      <PopoverContent className=''>
        {isAssignUserList ? (
          responsibleArr?.map((user: any) => {
            return (
              <div key={user?.id}>
                <div
                  key={user.value}
                  className='my-1 flex flex-col items-center gap-2 rounded-md p-1'
                >
                  <div className='flex w-full items-center justify-start gap-2'>
                    <Avatar className='h-7 w-7'>
                      <AvatarImage
                        src={
                          user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                        }
                      />
                    </Avatar>{" "}
                    {user?.name}
                  </div>
                </div>
                <Separator className='my-0.5' />
              </div>
            )
          })
        ) : (
          <>
            <Input
              id='column-name'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={translate("Search User")}
            />
            <Separator className='my-2' />
            <div className='max-h-[250px] min-w-fit overflow-y-auto'>
              {availableUsersArr.length > 0 ? (
                availableUsersArr.map((user: DictType, index: number) => {
                  return (
                    <div key={user?.id}>
                      <div
                        key={user.value}
                        className='my-1 flex cursor-pointer flex-row items-center gap-2 rounded-md p-1 hover:bg-primary/30'
                      >
                        <Checkbox
                          id={`user_${user?.id}`}
                          checked={selectedRows.has(user.id)}
                          onCheckedChange={() => {
                            handleCheckboxChange(user.id)
                            setSelectedUser(user)
                          }}
                        />
                        <Label
                          htmlFor={`user_${user?.id}`}
                          className='flex w-full cursor-pointer items-center justify-start gap-2'
                        >
                          <Avatar className='h-7 w-7'>
                            <AvatarImage
                              src={
                                user?.user_picture?.url ||
                                `${process.env.PUBLIC_URL}/user-profile.svg`
                              }
                            />
                          </Avatar>{" "}
                          {user?.name}
                        </Label>
                      </div>
                      {index != availableUsersArr.length - 1 ? (
                        <Separator className='my-0.5' />
                      ) : null}
                    </div>
                  )
                })
              ) : (
                <p>{translate("No Users")}</p>
              )}
            </div>
            <Separator className='my-2' />
            <PopoverClose className=' '>
              <Button onClick={handleSubmit}>{translate("Submit")}</Button>
            </PopoverClose>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default AssignToUser
